// Settings > Support: send a short bug report or suggestion (POST /api/me/feedback).
// Admins read them in Settings > Account (AdminFeedback below the admin tools).
import { useState, type FormEvent } from "react";
import { FEEDBACK_MAX, type FeedbackKind } from "@wp/shared";
import { api } from "../../net/api";

const KINDS: { id: FeedbackKind; label: string }[] = [
  { id: "bug", label: "Bug report" },
  { id: "suggestion", label: "Suggestion" },
];

type Status = { kind: "ok" | "error"; text: string } | null;

export function FeedbackBox({ signedIn }: { signedIn: boolean }) {
  const [kind, setKind] = useState<FeedbackKind>("bug");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status>(null);

  if (!signedIn) {
    return (
      <p className="muted small group-box" data-testid="feedback-logged-out">
        Log in to send a bug report or suggestion.
      </p>
    );
  }

  const empty = message.trim() === "";

  async function send(e: FormEvent) {
    e.preventDefault();
    if (empty || busy) return;
    setBusy(true);
    const res = await api.sendFeedback(kind, message.trim());
    setBusy(false);
    if (res.ok) {
      setMessage("");
      setStatus({ kind: "ok", text: kind === "bug" ? "Thanks! Your bug report was sent." : "Thanks! Your suggestion was sent." });
    } else {
      setStatus({ kind: "error", text: res.error.message });
    }
  }

  return (
    <form className="stack-form group-box feedback-box" onSubmit={send} noValidate data-testid="feedback-box">
      <h3>Report a bug / Suggestion</h3>
      <div className="segmented" role="radiogroup" aria-label="Feedback type">
        {KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            role="radio"
            aria-checked={kind === k.id}
            className={kind === k.id ? "seg active" : "seg"}
            onClick={() => setKind(k.id)}
            data-testid={`feedback-kind-${k.id}`}
          >
            {k.label}
          </button>
        ))}
      </div>
      <label className="field">
        <span>{kind === "bug" ? "What went wrong?" : "Your idea"}</span>
        <textarea
          rows={4}
          maxLength={FEEDBACK_MAX}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            if (status?.kind === "ok") setStatus(null);
          }}
          placeholder={kind === "bug" ? "What happened, and what did you expect?" : "What would make Wizard Poker better?"}
          data-testid="feedback-message"
        />
      </label>
      <div className="feedback-foot">
        <span className="feedback-count" aria-live="polite" data-testid="feedback-count">
          {message.length}/{FEEDBACK_MAX}
        </span>
        <button type="submit" className="btn primary" disabled={busy || empty} data-testid="feedback-send">
          {busy ? "Sending..." : "Send"}
        </button>
      </div>
      {status && (
        <p className={status.kind === "ok" ? "form-ok" : "form-error"} role={status.kind === "ok" ? "status" : "alert"} data-testid="feedback-status">
          {status.text}
        </p>
      )}
    </form>
  );
}
